import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputBase from '@mui/material/InputBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';

import { DemoApiError } from '../api/client';
import { useAccount } from '../account/AccountContext';
import { useSelection } from '../selection/SelectionContext';
import { useSession } from '../session/SessionContext';
import { brand } from '../theme';
import {
  DISPLAY_CAP_PER_TX_USD,
  DISPLAY_CAP_ROLLING_24H_USD,
  loadLastWithdrawAddress,
  saveLastWithdrawAddress,
} from './lastAddress';

function withdrawErrorCopy(err: unknown): string {
  if (err instanceof DemoApiError) {
    if (err.code === 'CHEDDAR_RISK_BLOCKED') {
      return 'Cheddar blocked this destination address (451 risk). Try a different address or check the demo org risk thresholds.';
    }
    if (err.code === 'CHEDDAR_FORBIDDEN') {
      return 'Cheddar rejected this request (403). The demo org likely has an IP allowlist enabled — turn it off for the Lambda egress.';
    }
    if (
      err.code === 'INSUFFICIENT_DEMO_BALANCE' ||
      err.code === 'CAP_EXCEEDED' ||
      err.code === 'INVALID_ADDRESS'
    ) {
      // Server message already includes requested / remaining / cap numbers.
      return err.message;
    }
    return err.message;
  }
  return err instanceof Error ? err.message : 'Withdraw failed';
}

function roughAddressOk(chain: string, address: string): boolean {
  const a = address.trim();
  if (!a) return false;
  switch (chain.toUpperCase()) {
    case 'ETHEREUM':
    case 'BSC':
    case 'POLYGON':
    case 'ROBINHOOD':
      return /^0x[a-fA-F0-9]{40}$/.test(a);
    case 'SOLANA':
      return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(a);
    case 'BITCOIN':
      return /^(bc1|[13]|tb1|[mn2])[a-zA-HJ-NP-Z0-9]{14,71}$/i.test(a);
    case 'BITCOIN_CASH':
      return /^((bitcoincash:)?[qp][02-9ac-hj-np-z]{41}|[13][1-9A-HJ-NP-Za-km-z]{25,34})$/i.test(a);
    case 'LITECOIN':
      return /^(ltc1[02-9ac-hj-np-z]{8,87}|[LM3][1-9A-HJ-NP-Za-km-z]{25,34})$/i.test(a);
    case 'TRON':
      return /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(a);
    default:
      return a.length >= 10;
  }
}

function capAmountInTokens(usdPrice: string): string | null {
  const p = Number(usdPrice);
  const cap = Number(DISPLAY_CAP_PER_TX_USD);
  if (!Number.isFinite(p) || p <= 0 || !Number.isFinite(cap)) return null;
  return (cap / p).toFixed(8).replace(/\.?0+$/, '');
}

function multiplyUsd(amount: string, usdPrice: string): string | null {
  if (!amount.trim() || Number.isNaN(Number(amount))) return null;
  const a = Number(amount);
  const p = Number(usdPrice);
  if (!Number.isFinite(a) || !Number.isFinite(p)) return null;
  return (a * p).toFixed(2);
}

export type WithdrawSubmitResult = {
  transactionId: string;
  status: 'SUBMITTED';
};

export interface WithdrawPanelProps {
  /** Optional hook for status UI; defaults to api.withdraw. */
  onSubmitted?: (result: WithdrawSubmitResult) => void;
  /** When true, omit the outer card shell (parent already provides one). */
  embedded?: boolean;
}

/** Shared geometry for the two withdraw inputs. */
const fieldSx = {
  height: 52,
  borderRadius: `${brand.controlRadius}px`,
  border: `1px solid ${brand.border}`,
  backgroundColor: brand.canvasSunk,
  px: 1.75,
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  transition: 'border-color .15s ease, box-shadow .15s ease',
  '&:focus-within': {
    borderColor: brand.gold,
    backgroundColor: brand.surface,
    boxShadow: `0 0 0 4px ${brand.goldWash}`,
  },
} as const;

const fieldLabelSx = { fontSize: 12.5, fontWeight: 600, color: brand.body } as const;

export default function WithdrawPanel({ onSubmitted, embedded = false }: WithdrawPanelProps) {
  const { session, api } = useSession();
  const { selection } = useSelection();
  const { account } = useAccount();

  const [amount, setAmount] = useState('');
  const [toAddress, setToAddress] = useState('');
  const [clientError, setClientError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<{ amount: string; toAddress: string } | null>(
    null,
  );

  const chain = selection?.chain ?? '';
  const mintId = selection?.mintId;
  const balance =
    mintId != null && account
      ? (account.customerBalances[String(mintId)] ?? '0')
      : '0';

  useEffect(() => {
    if (!chain) return;
    setToAddress(loadLastWithdrawAddress(chain));
    setAmount('');
    setClientError(null);
    setServerError(null);
    setSubmitted(null);
  }, [chain, mintId]);

  const usdApprox = useMemo(() => {
    if (!selection) return null;
    return multiplyUsd(amount, selection.usdPrice);
  }, [amount, selection]);

  if (!selection || !session) return null;

  const validateClient = (): string | null => {
    if (!amount.trim()) return 'Enter an amount';
    if (Number(amount) <= 0 || Number.isNaN(Number(amount))) {
      return 'Amount must be a positive number';
    }
    if (Number(amount) > Number(balance)) {
      return `Amount exceeds available balance (${balance} ${selection.symbol})`;
    }
    if (!toAddress.trim()) return 'Enter a destination address';
    if (!roughAddressOk(selection.chain, toAddress)) {
      return `Address does not look valid for ${selection.chain}`;
    }
    return null;
  };

  const handleMax = () => {
    // Lesser of the per-tx USD cap and what the customer actually holds.
    const capAmount = capAmountInTokens(selection.usdPrice);
    const max =
      capAmount && Number(capAmount) < Number(balance) ? capAmount : balance;
    setAmount(max);
    setClientError(null);
  };

  const handleSubmit = async () => {
    const err = validateClient();
    if (err) {
      setClientError(err);
      return;
    }
    setClientError(null);
    setServerError(null);
    setSubmitting(true);
    try {
      const result = await api.withdraw({
        customerId: session.customerId,
        mintId: selection.mintId,
        amount: amount.trim(),
        toAddress: toAddress.trim(),
      });
      saveLastWithdrawAddress(selection.chain, toAddress);
      setSubmitted({ amount: amount.trim(), toAddress: toAddress.trim() });
      setAmount('');
      setServerError(null);
      onSubmitted?.(result);
    } catch (e) {
      setServerError(withdrawErrorCopy(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Box data-testid="withdraw-panel">
        <Stack alignItems="center" spacing={1.5} sx={{ textAlign: 'center', py: 1.5 }}>
          <Box
            component="span"
            aria-hidden
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              backgroundColor: brand.goldWash,
              color: brand.goldDeep,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
              fontWeight: 800,
              animation: 'chd-pulse 2.2s ease-in-out infinite',
            }}
          >
            ⏱
          </Box>
          <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}>
            Awaiting merchant approval
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              color: brand.muted,
              lineHeight: 1.55,
              textWrap: 'pretty',
              maxWidth: 380,
            }}
          >
            Your withdrawal of{' '}
            <strong style={{ color: brand.ink }}>
              {submitted.amount} {selection.symbol}
            </strong>{' '}
            to{' '}
            <Box component="span" sx={{ fontFamily: brand.mono, fontSize: 13, color: brand.ink }}>
              {submitted.toAddress}
            </Box>{' '}
            is queued. It stays pending until the send confirms on-chain — watch the activity
            list below.
          </Typography>
          <Button
            variant="contained"
            onClick={() => setSubmitted(null)}
            sx={{ mt: 1, minWidth: 200 }}
          >
            New withdrawal
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box data-testid={embedded ? 'withdraw-panel' : 'withdraw-card'}>
      {!embedded ? (
        <Typography sx={{ fontSize: 13, color: brand.faint, mb: 1 }}>
          Treasury {balance} {selection.symbol}
        </Typography>
      ) : (
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: brand.faint, mb: 1.5 }}>
          Available {balance} {selection.symbol}
        </Typography>
      )}

      <Stack spacing={1.5} sx={{ mt: embedded ? 0 : 0.5 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.75}
          alignItems="stretch"
        >
          <Stack spacing={0.875} sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="label" htmlFor="withdraw-amount" sx={fieldLabelSx}>
              Amount
            </Typography>
            <Box sx={fieldSx}>
              <InputBase
                id="withdraw-amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                inputMode="decimal"
                disabled={submitting}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 20,
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: brand.ink,
                }}
              />
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: brand.muted }}>
                {selection.symbol}
              </Typography>
              <Button
                onClick={handleMax}
                disabled={submitting}
                sx={{
                  flex: 'none',
                  height: 28,
                  minWidth: 0,
                  px: 1.25,
                  borderRadius: '8px',
                  border: `1px solid ${brand.border}`,
                  backgroundColor: brand.surface,
                  fontSize: 12,
                  fontWeight: 700,
                  color: brand.goldDeep,
                  '&:hover': { backgroundColor: brand.goldWash },
                }}
              >
                MAX
              </Button>
            </Box>
          </Stack>

          <Stack spacing={0.875} sx={{ flex: 1.4, minWidth: 0 }}>
            <Typography component="label" htmlFor="withdraw-address" sx={fieldLabelSx}>
              Destination address ({selection.chain})
            </Typography>
            <Box sx={fieldSx}>
              <InputBase
                id="withdraw-address"
                value={toAddress}
                onChange={(e) => setToAddress(e.target.value)}
                placeholder="0x…"
                autoComplete="off"
                disabled={submitting}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontFamily: brand.mono,
                  fontSize: 13.5,
                  color: brand.ink,
                }}
              />
            </Box>
          </Stack>
        </Stack>

        {usdApprox != null && amount.trim() ? (
          <Typography sx={{ fontSize: 12.5, color: brand.faint }}>
            ≈ ${usdApprox} USD at ${selection.usdPrice}/{selection.symbol}
          </Typography>
        ) : null}

        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box
            component="span"
            aria-hidden
            sx={{
              flex: 'none',
              width: 15,
              height: 15,
              mt: '2px',
              borderRadius: '50%',
              border: `1.5px solid #C9D0D8`,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              fontWeight: 700,
              color: brand.faint,
            }}
          >
            i
          </Box>
          <Typography sx={{ fontSize: 12.5, color: brand.faint, lineHeight: 1.5 }}>
            Every withdrawal needs merchant approval before funds leave. Demo limits: $
            {DISPLAY_CAP_PER_TX_USD} per transaction, ${DISPLAY_CAP_ROLLING_24H_USD} per
            24 hours.
          </Typography>
        </Stack>

        {clientError ? <Alert severity="warning">{clientError}</Alert> : null}
        {serverError ? <Alert severity="error">{serverError}</Alert> : null}

        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={submitting}
          fullWidth
          sx={{ height: 52, fontSize: 16 }}
        >
          {submitting ? 'Submitting…' : 'Submit withdrawal'}
        </Button>
      </Stack>
    </Box>
  );
}
